/*
 * The autocomplete sources chat hands its composer. One combobox serves every trigger (spec 12
 * §4.4): the module owns the "#" source over the conversations the reader can write about, and hands
 * the composer's own "@" source the slices it needs — the members of *this* conversation first, then
 * everybody else, then the mentionable groups. A source is data and a match rule, never a widget.
 *
 * Every query reads the store when it runs rather than closing over a snapshot, so a source object
 * is built once per conversation and never again: nothing re-renders because a source was rebuilt,
 * and the "@" list still follows the store.
 */

import { peopleSource } from "../../../core/composer/index.ts"
import type { AutocompleteItem, AutocompleteSource } from "../../../core/composer/types.ts"
import { client } from "../../../core/store/store.ts"
import { t } from "../i18n/t.ts"
import { browseChannels, type ChannelSummary } from "./api.ts"

const MAX_ITEMS = 8

/** How long the public directory is believed. A channel created a minute ago is findable by name. */
const DIRECTORY_TTL_MS = 60_000

/** The public directory as it was last answered, and when — module state, like the switcher's. */
let directory: ChannelSummary[] = []
let directoryAt = 0
let directoryInFlight = false

/**
 * Asks for the public directory unless a fresh answer is already held. It returns at once and never
 * throws: a source that awaited the network would close the list on every keystroke while the answer
 * was in flight, and a browse nobody asked for is not a failure to report — the reader's own
 * conversations are in the list either way.
 */
function refreshDirectory(): void {
	if (directoryInFlight || Date.now() - directoryAt < DIRECTORY_TTL_MS) return
	directoryInFlight = true
	void browseChannels("")
		.then((answer) => {
			directory = answer.channels
			directoryAt = Date.now()
		})
		.catch(() => undefined)
		.finally(() => {
			directoryInFlight = false
		})
}

/** Forgets the public directory, so the next query asks again. The tests' way back to a clean list. */
export function forgetBrowsableChannels(): void {
	directory = []
	directoryAt = 0
}

/** A conversation is found by its address or by the name people call it out loud. */
function matches(needle: string, slug: string, name: string): boolean {
	return slug.toLowerCase().includes(needle) || name.toLowerCase().includes(needle)
}

/**
 * "#" over conversations: the slug is what gets inserted, the name is what the reader searches.
 *
 * Two lists feed it, in this order. The reader's own conversations come out of the store and are
 * therefore offered in the frame the character was typed in. Behind them stand the public channels
 * they are *not* in — the ones they most need a word for, and the only ones the model cannot answer —
 * read from the browse route, the same action the browse dialog and the quick switcher use, so the
 * source needs no permission of its own. A row of the second kind says that the reader is not a
 * member, because it is also the row whose reference will not be a link for everybody (X-A4 N6).
 */
const channelSource: AutocompleteSource = {
	trigger: "#",
	minChars: 1,
	query(term: string): Promise<AutocompleteItem[]> {
		refreshDirectory()
		const needle = term.toLowerCase()
		const items: AutocompleteItem[] = []
		const taken = new Set<string>()
		for (const slice of Object.values(client.api.getState().conversations)) {
			const { meta } = slice
			if (meta.kind !== "channel" && meta.kind !== "private") continue
			// A named conversation always carries both; the guard is what makes that a fact here
			// rather than an assumption, because the wire omits either on a dm.
			const slug = meta.slug ?? ""
			const name = meta.name ?? ""
			if (slug === "" || !matches(needle, slug, name)) continue
			taken.add(slug)
			items.push({ id: slug, label: `#${slug}`, detail: name })
			if (items.length === MAX_ITEMS) return Promise.resolve(items)
		}
		for (const row of directory) {
			const slug = row.conversation.slug ?? ""
			if (slug === "" || row.joined || taken.has(slug)) continue
			const name = row.conversation.name ?? slug
			if (!matches(needle, slug, name)) continue
			items.push({ id: slug, label: `#${slug}`, detail: t("chat.switcher.joinable", { name }) })
			if (items.length === MAX_ITEMS) break
		}
		return Promise.resolve(items)
	},
	insert: (item: AutocompleteItem) => `#${item.id} `,
}

/** The sources of one conversation, built once per conversation and cached by its id. */
const byConversation = new Map<number, AutocompleteSource[]>()

/** The composer's "@" source, reading this conversation's members out of the store per query. */
function peopleSlicesSource(conv: number): AutocompleteSource {
	return peopleSource(() => {
		const model = client.api.getState()
		return {
			users: model.users,
			groups: model.groups,
			memberIds: model.conversations[conv]?.meta.memberIds ?? [],
			meId: model.me?.id,
		}
	})
}

/**
 * Every source the chat surface hands its composer, for one conversation. Without the "@" source
 * registered here, mentioning a person was reachable by no means at all — and with it everything
 * behind a mention: the notification levels, @channel, the group chip the list already draws.
 *
 * Opening a conversation is also when the public directory is warmed, so the first "#" somebody
 * types already offers the channels they are not in instead of learning about them one keystroke late.
 */
export function chatSourcesFor(conv: number): AutocompleteSource[] {
	refreshDirectory()
	const cached = byConversation.get(conv)
	if (cached) return cached
	const sources = [peopleSlicesSource(conv), channelSource]
	byConversation.set(conv, sources)
	return sources
}
